import type { LotoPanelState } from 'features/knowledgeDocumentViewer/types'
import { useCallback } from 'react'

import type { LotoReportScope } from './lotoReportSchemas'
import { type LotoLeavePrompt, useLotoLeavePrompt } from './useLotoLeavePrompt'
import {
  type LotoPageAddition,
  useLotoPageAddition,
} from './useLotoPageAddition'
import { type LotoSession, useLotoSession } from './useLotoSession'

export type SelectLoto = (
  reportId: string | undefined,
  onSelected?: () => void
) => void

export type UseLotoPanelSessionResult = {
  session: LotoSession | undefined
  leavePrompt: LotoLeavePrompt
  addition: LotoPageAddition
  select: SelectLoto
  close: () => void
}

export function useLotoPanelSession(
  scope: LotoReportScope | undefined,
  activeReportId: string | undefined,
  setPanel: (next: LotoPanelState | undefined) => void
): UseLotoPanelSessionResult {
  const session = useLotoSession(scope, activeReportId)
  const leavePrompt = useLotoLeavePrompt(session)
  const addition = useLotoPageAddition()
  const { requestLeave } = leavePrompt
  const { reset } = addition
  const select = useCallback<SelectLoto>(
    (reportId, onSelected) => {
      if (reportId !== undefined && reportId === activeReportId)
        return onSelected?.()
      requestLeave(() => {
        reset()
        setPanel(reportId === undefined ? {} : { activeReportId: reportId })
        onSelected?.()
      })
    },
    [activeReportId, requestLeave, reset, setPanel]
  )
  const close = useCallback(
    () =>
      requestLeave(() => {
        reset()
        setPanel(undefined)
      }),
    [requestLeave, reset, setPanel]
  )
  return { session, leavePrompt, addition, select, close }
}
